import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { rwColors } from '@/styles/colors';
import { NodeSingular } from 'cytoscape';
import { LoaderScreen } from '@/components/Elements/LoaderScreen';
import { useTranslateSearchText } from '@/features/analyze/api/translateSearchText';
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { Toolbar } from '@/features/connect-dots/components/visualizer/ToolBar';
import { BaseSlider } from '@/features/connect-dots/components/Slider';
import NetworkContainer from '@/features/connect-dots/components/containers/NetworkContainer';
import {
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_STEP,
} from '@/features/connect-dots/types/cytoscape';
import { useNetworkState } from '@/features/connect-dots/hooks/useNetworkState';

import '@/features/connect-dots/styles/connectDots.css';

export const TagViewContainer = () => {
  const {
    isDataLoaded,
    filteredData,
    selectedNodeIds,
    cytoscapeCoreRefObject,
    updateNodeTypeFilter,
    nodeTypeFilters,
    networkError,
  } = useNetworkState();

  const { t } = useTranslation();

  const translateSearchMutation = useTranslateSearchText();

  const [searchText, setSearchText] = useState('');
  const blinkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ズーム関連のstateとハンドラを追加
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoomChange = useCallback(
    (value: number) => {
      setZoomLevel(value);
      if (cytoscapeCoreRefObject.current) {
        cytoscapeCoreRefObject.current.zoom(value);
      }
    },
    [cytoscapeCoreRefObject]
  );

  // cytoscape の zoom イベントと初期値同期
  useEffect(() => {
    let rafId: number | null = null;
    let attachedCy: cytoscape.Core | null = null;
    let mounted = true;

    const attachWhenReady = () => {
      if (!mounted) return;
      const cy = cytoscapeCoreRefObject?.current;
      if (!cy) {
        // cytoscape の生成を待つ
        rafId = requestAnimationFrame(attachWhenReady);
        return;
      }

      attachedCy = cy;

      // 初期値を同期（クランプ）
      const initialZoom = Number(cy.zoom().toFixed(2));
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, initialZoom));
      setZoomLevel(clampedZoom);

      // 現在のズームレベルを監視する関数
      const onZoom = () => {
        const currentZoom = Number(cy.zoom().toFixed(2));
        const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, currentZoom));
        setZoomLevel(clamped);
      };

      cy.on('zoom', onZoom);
    };

    rafId = requestAnimationFrame(attachWhenReady);

    return () => {
      mounted = false;
      if (rafId != null) cancelAnimationFrame(rafId);
      if (attachedCy) {
        try {
          if (attachedCy.off) {
            attachedCy.off('zoom');
          }
        } catch (e) {
          // ignore
          console.error('Failed to detach zoom event:', e);
        }
      }
    };
  }, [cytoscapeCoreRefObject]);

  // ★ エラーハンドリング
  if (networkError) {
    console.error('Network data error:', networkError);
  }

  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(
    undefined
  );

  // ★ ローディング状態の統合
  const isDataReady = useMemo(() => {
    return isDataLoaded && filteredData;
  }, [isDataLoaded, filteredData]);

  const showLoader = !isDataReady;

  // NetworkContainerをメモ化
  const memoizedNetworkContainer = useMemo(
    () => (
      <NetworkContainer
        filteredData={filteredData}
        cytoscapeCoreRefObject={cytoscapeCoreRefObject}
        selectedNodeIds={selectedNodeIds}
      />
    ),
    [filteredData, cytoscapeCoreRefObject, selectedNodeIds]
  );

  // 検索実行関数
  const handleNodeSearch = async () => {
    if (!cytoscapeCoreRefObject.current) return;
    const cy = cytoscapeCoreRefObject.current;

    // 既存のハイライトをリセット
    cy.nodes('.searched').removeClass('searched');

    // 既存の点滅アニメーションを解除
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
      cy.nodes().removeClass('dimmed');
    }

    // 検索テキストが空の場合は何もしない
    if (!searchText.trim()) {
      return;
    }

    // ここで翻訳
    let translated = searchText.toLowerCase();
    try {
      const result: { translatedText: string } =
        await translateSearchMutation.mutateAsync({
          text: searchText,
        });

      if (!result?.translatedText) {
        console.error(t('翻訳中にエラーが発生しました'));
        return;
      } else {
        translated = result.translatedText.toLowerCase();
      }
    } catch (e) {
      // エラー時は元のテキストで検索
      console.error('翻訳APIエラー', e);
    }

    if (translated === 'dummy_translated_text') {
      translated = searchText.toLowerCase();
    }

    // labelまたはkeywordsに部分一致するノードを検索
    const matched = cy.nodes().filter((ele: NodeSingular) => {
      const label = ele.data('label').toLowerCase() || '';
      const keywords = (ele.data('keywords') || []).join(' ');
      return (
        label.includes(translated) ||
        keywords.toLowerCase().includes(translated)
      );
    });

    // マッチしたノードにクラス付与（CSS用）
    matched.addClass('searched');
    matched.style('display', 'element'); // 検索結果として表示
    blinkIntervalRef.current = setInterval(() => {
      matched.toggleClass('dimmed'); // dimmedクラスをトグルして点滅アニメーションを適用
    }, 500); // 500msごと
  };

  if (!filteredData?.nodes.length) {
    return <LoaderScreen contained={true} />;
  }

  return (
    <div className="size-full">
      {showLoader ? (
        <LoaderScreen contained={true} />
      ) : (
        <div className="relative w-full">
          {/* ツールバー */}
          <Toolbar
            searchText={searchText}
            setSearchText={setSearchText}
            handleNodeSearch={handleNodeSearch}
            cytoscapeCoreRefObject={cytoscapeCoreRefObject}
            selectedThemeId={selectedThemeId}
            setSelectedThemeId={setSelectedThemeId}
            filteredData={filteredData}
            nodeTypeFilters={nodeTypeFilters}
            updateNodeTypeFilter={updateNodeTypeFilter}
          />
          {/* ネットワーク図をメモ化したものに差し替え */}
          <div className="relative flex h-full min-h-[300px]">
            {memoizedNetworkContainer}
            <div
              className="absolute right-4 top-1/2 z-20 -translate-y-1/2"
              style={{ borderRadius: '50%' }}
            >
              <BaseSlider
                value={zoomLevel}
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={ZOOM_STEP}
                label1={
                  <button
                    type="button"
                    className="cursor-pointer focus:outline-none"
                    onClick={() => {
                      if (cytoscapeCoreRefObject.current) {
                        cytoscapeCoreRefObject.current.zoom(
                          Math.min(
                            cytoscapeCoreRefObject.current.zoom() + ZOOM_STEP,
                            MAX_ZOOM
                          )
                        );
                      }
                    }}
                    tabIndex={0}
                  >
                    <PlusCircleIcon
                      className="size-6 stroke-white"
                      style={{ fill: rwColors['dark-gray'] }}
                    />
                  </button>
                }
                label2={
                  <button
                    type="button"
                    className="cursor-pointer focus:outline-none"
                    onClick={() => {
                      if (cytoscapeCoreRefObject.current) {
                        cytoscapeCoreRefObject.current.zoom(
                          Math.max(
                            cytoscapeCoreRefObject.current.zoom() - ZOOM_STEP,
                            MIN_ZOOM
                          )
                        );
                      }
                    }}
                    tabIndex={0}
                  >
                    <MinusCircleIcon
                      className="size-6 stroke-white"
                      style={{ fill: rwColors['dark-gray'] }}
                    />
                  </button>
                }
                onChange={handleZoomChange}
                vertical={true}
                trackColor={rwColors.gray}
                fillColor={rwColors.main}
                thumbColor={rwColors.sub}
                className="rounded-lg bg-white/10 p-1 shadow-lg backdrop-blur-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
